import axios from 'axios';
import { AGM_API_PATH } from '@/constants/agm';
import { fakeAgmApi, isAgmHarnessActive } from '@/utils/agm-harness';

// The AGM RSVP's calls to hogc-one-api: `<VUE_APP_API_URL>/v2/agm` (VUE_APP_API_URL
// already ends in /api). Contract: §4.2 of
// docs/plans/2026-10-22-agm-rsvp-mobile-migration.md. While the dev harness is on
// (/agm-states) the calls go to its fake instead, which speaks the same shapes.
//
// Both calls answer with the whole screen, and the response body is member data (the
// answer, whether they are eligible), so nothing here logs it or an axios error,
// whose `response` and `config` carry it. Callers get the status code and nothing else.

const base = () => `${process.env.VUE_APP_API_URL}${AGM_API_PATH}`;
const auth = token => ({ headers: { Authorization: `Bearer ${token}` } });

async function call(fake, real) {
  const response = isAgmHarnessActive() ? await fake() : await real();
  return response?.data;
}

/**
 * GET /v2/agm: the whole screen. 200 for any signed-in member (a member who is not
 * eligible gets `{ eligible: false, agmActive }`); the flag being off is a 404.
 */
export function fetchAgm(token) {
  return call(
    () => fakeAgmApi.fetchAgm(),
    () => axios.get(base(), auth(token)),
  );
}

/**
 * PUT /v2/agm/rsvp `{ attending }`. Resolves with the whole screen, as GET does, or
 * with `{ closed: true }` on a 409: RSVP closed (the cutoff passed, or ops closed it)
 * while the member was answering. Any other failure rejects.
 */
export async function putRsvp(token, attending) {
  try {
    return await call(
      () => fakeAgmApi.putRsvp(attending),
      () => axios.put(`${base()}/rsvp`, { attending }, auth(token)),
    );
  } catch (error) {
    if (error?.response?.status === 409) return { closed: true };
    throw error;
  }
}

/**
 * The only thing worth saying about a failed call: its HTTP status, or null when it
 * never got one (offline, timed out). Safe to log.
 */
export function agmErrorStatus(error) {
  return error?.response?.status ?? null;
}
